import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { estadoDeCobro, hayQueReleerPorLaBandera, type EstadoDeCobro } from "../lib/estadoDeCobro";
import { conCobroDeMarcas, leerRechazoDeCobro } from "../lib/cobroRechazado";
import { alCambiarElUltimoSaldo, asegurarSaldoLeido, ultimoSaldoLeido } from "../lib/saldo";
import { conSimulacion } from "../lib/simulacionDeCobro";

/**
 * El estado de cobro de la cuenta para quien no tiene ya la fila de la empresa a mano: hoy, la
 * sidebar de escritorio (`EstiloMinimal`), que cambia "Ver planes" por "Pago rechazado"
 * (ADR-2026-10-26-cobros-rechazados-en-la-plataforma). La home y /subscription lo calculan sobre la
 * consulta que ya hacen.
 *
 * El shell no se desmonta al navegar, así que leer sólo al montar dejaba "Pago rechazado" después de
 * pagar (hallazgo de Greptile, PR #208). Se vuelve a leer:
 *  - con el evento `EVENTO_COBRO_ACTUALIZADO` (lo dispara /subscription al cobrar), y
 *  - cuando la pestaña vuelve a estar visible (vuelta de MercadoPago o de otra pestaña).
 * Un fallo deja el último estado conocido; sin ninguno, `null` (= sin aviso, "Ver planes").
 */
export const EVENTO_COBRO_ACTUALIZADO = "atlas:cobro-actualizado";

/** Avisa a quien muestre el estado de cobro que lo vuelva a leer (ej. después de "Pagar ahora"). */
export function avisarCobroActualizado(): void {
  window.dispatchEvent(new Event(EVENTO_COBRO_ACTUALIZADO));
}

export function useEstadoDeCobro(): EstadoDeCobro | null {
  const [estado, setEstado] = useState<EstadoDeCobro | null>(() => conSimulacion(null));
  const vigente = useRef(true);
  // Gana la última lectura que empezó: la que dispara la bandera puede cruzarse con otra.
  const ultimaCarga = useRef(0);
  // ¿La última lectura ya supo que la empresa factura por marca? Si no lo supo (el saldo tardó más
  // que el tope de `asegurarSaldoLeido`), se relee cuando el saldo llega con la bandera (review
  // Greptile #221, ADR-2026-10-29-cobro-rechazado-por-marca-2).
  const leyoConBandera = useRef(false);

  const cargar = useCallback(async () => {
    const mia = ++ultimaCarga.current;
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;
      const { data: perfil } = await supabase
        .from("profiles")
        .select("id_company")
        .eq("id", user.id)
        .maybeSingle();
      const idCompany = perfil?.id_company as string | undefined;
      if (!idCompany) return;
      const { data: empresa } = await supabase
        .from("company_profiles")
        .select("plan, plataforma_pago, trigger_cancelacion, fecha_cancelacion_programada, paid_plan_cancel_date")
        .eq("id_company", idCompany)
        .maybeSingle();
      if (!empresa) return;
      const plan = (empresa.plan as string | null) ?? null;
      const rechazo = plan && plan !== "gratis" ? await leerRechazoDeCobro(idCompany) : {};
      // Con facturación por marca, el aviso es el de las marcas (ADR-2026-10-29-cobro-rechazado-por-marca).
      // La bandera sale del saldo que el shell ya leyó; si tarda, del último conocido.
      const saldo = (await asegurarSaldoLeido()) ?? ultimoSaldoLeido();
      leyoConBandera.current = saldo?.facturaPorMarca === true;
      const cobro = await conCobroDeMarcas(
        estadoDeCobro({
          id_company: idCompany,
          plan,
          plataforma_pago: (empresa.plataforma_pago as string | null) ?? null,
          trigger_cancelacion: (empresa.trigger_cancelacion as string | null) ?? null,
          fecha_cancelacion_programada: (empresa.fecha_cancelacion_programada as string | null) ?? null,
          paid_plan_cancel_date: (empresa.paid_plan_cancel_date as string | null) ?? null,
          ...rechazo,
        }),
        idCompany,
        saldo?.facturaPorMarca === true,
      );
      if (!vigente.current || mia !== ultimaCarga.current) return;
      setEstado(conSimulacion(cobro));
    } catch {
      /* queda el último estado conocido */
    }
  }, []);

  useEffect(() => {
    vigente.current = true;
    void cargar();
    const alVolver = () => {
      if (document.visibilityState === "visible") void cargar();
    };
    window.addEventListener(EVENTO_COBRO_ACTUALIZADO, cargar);
    document.addEventListener("visibilitychange", alVolver);
    // Sin la bandera nunca relee: una empresa sin facturación por marca no suma consultas.
    const dejarDeOir = alCambiarElUltimoSaldo(() => {
      if (hayQueReleerPorLaBandera(leyoConBandera.current, ultimoSaldoLeido())) void cargar();
    });
    return () => {
      vigente.current = false;
      dejarDeOir();
      window.removeEventListener(EVENTO_COBRO_ACTUALIZADO, cargar);
      document.removeEventListener("visibilitychange", alVolver);
    };
  }, [cargar]);

  return estado;
}
